import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Search, UserCheck, UserX, Trash2, Plus, X, Eye, EyeOff, KeyRound } from 'lucide-react';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionValue, setCommissionValue] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', password: '', first_name: '', last_name: '', commission_percent: 30 });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users); setTotal(data.total);
    } catch (err) { toast.error('Errore'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const activate = async (id) => {
    try { await api.post(`/admin/users/${id}/activate`); toast.success('Utente attivato!'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };
  const disable = async (id) => {
    if (!window.confirm('Disabilitare questo utente?')) return;
    try { await api.post(`/admin/users/${id}/disable`); toast.success('Utente disabilitato'); load(); }
    catch (err) { toast.error('Errore'); }
  };
  const deleteUser = async (id, name) => {
    if (!window.confirm(`Eliminare definitivamente l'utente ${name}? Questa azione non è reversibile.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success('Utente eliminato'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };
  const saveCommission = async (id) => {
    try { await api.put(`/admin/users/${id}/commission`, { commission_percent: parseFloat(commissionValue) }); toast.success('Commissione aggiornata'); setEditingCommission(null); load(); }
    catch (err) { toast.error('Errore'); }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$';
    let p = ''; for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    return p;
  };

  const openResetModal = (u) => {
    const p = generatePassword();
    setNewPass(p);
    setShowNewPass(true);
    setResetModal(u);
  };

  const resetPassword = async () => {
    if (!newPass || newPass.length < 8) return toast.error('Password minimo 8 caratteri');
    try {
      await api.post(`/admin/users/${resetModal.id}/reset-password`, { new_password: newPass });
      toast.success(`Password reimpostata per ${resetModal.first_name}`);
      setResetModal(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const inviteUser = async () => {
    if (!inviteForm.email || !inviteForm.password || !inviteForm.first_name || !inviteForm.last_name) return toast.error('Tutti i campi sono obbligatori');
    if (inviteForm.password.length < 8) return toast.error('Password minimo 8 caratteri');
    setSaving(true);
    try {
      await api.post('/admin/users/create', inviteForm);
      toast.success('Utente creato! Email inviata con le credenziali.');
      setShowInvite(false);
      setInviteForm({ email: '', password: '', first_name: '', last_name: '', commission_percent: 30 });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  const statusColors = { pending_verification: 'bg-gray-600', pending_activation: 'bg-yellow-600', active: 'bg-green-600', disabled: 'bg-red-600' };
  const statusLabels = { pending_verification: 'Verifica Email', pending_activation: 'Da Attivare', active: 'Attivo', disabled: 'Disabilitato' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Gestione Utenti <span className="text-gray-500 text-lg">({total})</span></h1>
        <button onClick={() => { const p = generatePassword(); setInviteForm(f => ({ ...f, password: p })); setShowInvite(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Invita Utente
        </button>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input className="input-field-dark pl-10" placeholder="Cerca per nome, email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input-field-dark w-48" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="pending_activation">Da Attivare</option>
          <option value="active">Attivi</option>
          <option value="disabled">Disabilitati</option>
        </select>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" /></div> : (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} className="card-dark p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-300">
                  {u.first_name?.[0]}{u.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-white">{u.first_name} {u.last_name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${statusColors[u.status]}`}>{statusLabels[u.status]}</span>
                    {u.role === 'admin' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-600 text-white">Admin</span>}
                  </div>
                  <p className="text-xs text-gray-400">{u.email} · {u.release_count} release{u.label_names ? ` · ${u.label_names}` : u.label_count > 0 ? ` · ${u.label_count} label` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white font-medium">€{parseFloat(u.available_balance || 0).toFixed(2)}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-gray-400">PN:</span>
                    {editingCommission === u.id ? (
                      <div className="flex items-center gap-1">
                        <input type="number" className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white"
                          value={commissionValue} onChange={e => setCommissionValue(e.target.value)} min="0" max="100" step="0.5" />
                        <button onClick={() => saveCommission(u.id)} className="text-xs text-green-400 hover:underline">OK</button>
                        <button onClick={() => setEditingCommission(null)} className="text-xs text-gray-500 hover:underline">X</button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingCommission(u.id); setCommissionValue(u.pn_commission_percent); }}
                        className="text-xs text-yellow-400 hover:underline">{u.pn_commission_percent}%</button>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {(u.status === 'pending_activation' || u.status === 'disabled') && (
                    <button onClick={() => activate(u.id)} className="p-2 text-green-400 hover:bg-green-400/10 rounded" title="Attiva">
                      <UserCheck size={18} />
                    </button>
                  )}
                  {u.status === 'active' && u.role !== 'admin' && (
                    <button onClick={() => disable(u.id)} className="p-2 text-red-400 hover:bg-red-400/10 rounded" title="Disabilita">
                      <UserX size={18} />
                    </button>
                  )}
                  {u.role !== 'admin' && (
                    <button onClick={() => openResetModal(u)} className="p-2 text-yellow-400/50 hover:text-yellow-400 hover:bg-yellow-400/10 rounded" title="Reset Password">
                      <KeyRound size={16} />
                    </button>
                  )}
                  {u.role !== 'admin' && (
                    <button onClick={() => deleteUser(u.id, `${u.first_name} ${u.last_name}`)} className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-400/10 rounded" title="Elimina">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1e1e2f] rounded-2xl p-6 w-full max-w-md shadow-xl border border-gray-700">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-lg text-white">Invita Nuovo Utente</h2>
              <button onClick={() => setShowInvite(false)} className="p-1 hover:bg-gray-700 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Nome *</label>
                  <input className="input-field-dark" value={inviteForm.first_name} onChange={e => setInviteForm(f => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Cognome *</label>
                  <input className="input-field-dark" value={inviteForm.last_name} onChange={e => setInviteForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email *</label>
                <input type="email" className="input-field-dark" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Password *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type={showPass ? 'text' : 'password'} className="input-field-dark pr-10" value={inviteForm.password}
                      onChange={e => setInviteForm(f => ({ ...f, password: e.target.value }))} />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button onClick={() => { const p = generatePassword(); setInviteForm(f => ({ ...f, password: p })); }} className="btn-secondary text-xs px-3 whitespace-nowrap">Genera</button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Commissione PN %</label>
                <input type="number" className="input-field-dark w-24" value={inviteForm.commission_percent}
                  onChange={e => setInviteForm(f => ({ ...f, commission_percent: parseFloat(e.target.value) }))} min="0" max="100" step="0.5" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowInvite(false)} className="btn-secondary flex-1">Annulla</button>
              <button onClick={inviteUser} disabled={saving} className="btn-primary flex-1">
                {saving ? 'Creazione...' : 'Crea e Invia Email'}
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-3 text-center">L'utente riceverà un'email con le credenziali di accesso</p>
          </div>
        </div>
      )}

      {resetModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1e1e2f] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg text-white">Reset Password</h2>
              <button onClick={() => setResetModal(null)} className="p-1 hover:bg-gray-700 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-400 mb-4">Nuova password per <strong className="text-white">{resetModal.first_name} {resetModal.last_name}</strong></p>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <input type={showNewPass ? 'text' : 'password'} className="input-field-dark pr-10" value={newPass} onChange={e => setNewPass(e.target.value)} />
                <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button onClick={() => setNewPass(generatePassword())} className="btn-secondary text-xs px-3">Genera</button>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setResetModal(null)} className="btn-secondary flex-1">Annulla</button>
              <button onClick={resetPassword} className="btn-primary flex-1">Reimposta</button>
            </div>
            <p className="text-[10px] text-gray-500 mt-3 text-center">Comunica la nuova password all'utente</p>
          </div>
        </div>
      )}
    </div>
  );
}
